import {
  normalizeInteractionText,
  notifyProductCardSelect,
  pushDataLayerEvent,
  type DataLayerWindow
} from './core';
import type { AnalyticsInteraction, InteractionDataLayerEvent } from './types';

type InteractionCandidate = {
  element: Element;
  interaction: AnalyticsInteraction;
};

const SPECIALIZED_INTERACTION_SELECTORS: ReadonlyArray<{
  interaction: AnalyticsInteraction;
  selector: string;
}> = [
  { interaction: 'hero-carousel-toggle', selector: '.hero-banner [data-hero-toggle]' },
  {
    interaction: 'hero-white-cta',
    selector:
      '.hero-banner .btn--primary-white, .cta-banner .btn--primary-white, .product-hero .btn--primary-white, .article-hero .btn--primary-white'
  },
  {
    interaction: 'video-hero-promo-cta',
    selector: '.video-hero .promo-card__link[href]'
  },
  {
    interaction: 'banner-outline-cta',
    selector:
      '.hero-banner .btn--secondary-outline, .cta-banner .btn--secondary-outline, .product-hero .btn--secondary-outline'
  },
  {
    interaction: 'flip-card-front',
    selector: '[data-flip-card-open], [data-flip-card-front]'
  },
  {
    interaction: 'flip-card-back',
    selector: '[data-flip-card-close], [data-flip-card-back]'
  },
  { interaction: 'story-card', selector: '.story-card[href]' },
  { interaction: 'events-news-card', selector: 'a.events-news-card[href]' },
  { interaction: 'latest-news-card', selector: '.latest-news-card__title-link[href]' },
  { interaction: 'solutions-card-link', selector: '.solutions-card__link[href]' },
  {
    interaction: 'generic-card-cta-link',
    selector:
      '.visual-builder-section--cta-link .generic-card__primary-link[href], .visual-builder-section--cta-link .generic-card__secondary-link[href]'
  },
  {
    interaction: 'industry-product-link',
    selector: '.sub-industry-card-list__product-link[href]'
  },
  {
    interaction: 'accordion-trigger',
    selector: '[data-accordion-trigger]'
  },
  {
    interaction: 'accordion-circle-toggle',
    selector: '[data-module="accordion"] [data-accordion-toggle]'
  },
  {
    interaction: 'solutions-card-circle-toggle',
    selector: '[data-solutions-see-more]'
  },
  {
    interaction: 'listing-circle-toggle',
    selector: '.all-products-listing__see-more'
  },
  {
    interaction: 'product-listing-white-cta',
    selector: '.all-products-listing--product-catalog .product-card a.btn--secondary-outline[href]'
  },
  {
    interaction: 'product-listing-black-cta',
    selector: '.all-products-listing--product-catalog .product-card a.btn--primary-black[href]'
  },
  {
    // Track the control inside the card, not the card: it is what the user
    // activates and what carries the authored href.
    interaction: 'pricing-info-card',
    selector: '.pricing-info-card__link'
  },
  {
    interaction: 'pricing-quantity-increase',
    selector: '[data-quantity-increase]'
  },
  {
    interaction: 'pricing-quantity-decrease',
    selector: '[data-quantity-decrease]'
  },
  {
    interaction: 'header-navigation-open',
    selector: '[data-nav-trigger], [data-mobile-nav-trigger]'
  },
  {
    interaction: 'header-promo-media',
    selector: '.mega-menu-featured-card__media a[href]'
  },
  { interaction: 'header-promo-text', selector: '.featured-card-link' },
  { interaction: 'header-cta-card', selector: '.mega-menu-icon-card' },
  { interaction: 'header-submenu-link', selector: '.secondary-menu-label' },
  { interaction: 'breadcrumb', selector: '.breadcrumbs__link' },
  { interaction: 'header-login', selector: '[data-login-link]' },
  { interaction: 'header-cart', selector: '[data-mini-cart-trigger]' },
  {
    interaction: 'header-language',
    selector: '[data-language-option], [data-mobile-utility-panel="language"] a[href]'
  },
  { interaction: 'header-search', selector: '[data-search-overlay-trigger]' },
  { interaction: 'footer-social', selector: '.footer-social-link' },
  { interaction: 'footer-link', selector: '.footer-column__link' }
];

const GLOBAL_INTERACTION_SELECTORS: ReadonlyArray<{
  interaction: AnalyticsInteraction;
  selector: string;
}> = [
  {
    interaction: 'global-text-cta',
    selector:
      '.btn--tertiary-text[href], .accordion__link[href], .search-results__read-more[href], .search-overlay__quick-link[href], .solutions-card__link[href], .sub-industry-card-list__product-link[href], .rtf a[href]:not(.btn)'
  },
  {
    interaction: 'global-black-cta',
    selector: 'a.btn--primary-black[href], a.btn--secondary-grey[href]'
  },
  {
    interaction: 'global-white-cta',
    selector: 'a.btn--primary-white[href], a.btn--secondary-outline[href]'
  }
];

function getAuthoredHref(element: Element): string {
  return element.getAttribute('href')?.trim() ?? '';
}

function normalizeInteractionLink(value: string, baseUrl?: string): string {
  if (!value || !baseUrl) return value;

  try {
    const resolvedUrl = new URL(value, baseUrl);
    if (resolvedUrl.protocol !== 'http:' && resolvedUrl.protocol !== 'https:') return value;

    const resolvedBaseUrl = new URL(baseUrl);
    return resolvedUrl.origin === resolvedBaseUrl.origin
      ? `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`
      : resolvedUrl.href;
  } catch {
    return value;
  }
}

function getChildText(element: Element, selector: string): string {
  return normalizeInteractionText(element.querySelector(selector)?.textContent);
}

function getClosestCardTitle(
  element: Element,
  cardSelector: string,
  titleSelector: string
): string {
  return normalizeInteractionText(
    element.closest(cardSelector)?.querySelector(titleSelector)?.textContent
  );
}

function getActiveHeroTitle(element: Element): string {
  return normalizeInteractionText(
    element
      .closest('.hero-banner')
      ?.querySelector('[data-hero-slide][data-active] [data-hero-heading]')?.textContent
  );
}

function getFlipCardTitle(element: Element): string {
  return normalizeInteractionText(
    element.closest('[data-flip-card]')?.querySelector('[data-flip-card-front] h3')?.textContent
  );
}

function getPromoCardTitle(element: Element): string {
  const card = element.closest('.promo-card');
  return normalizeInteractionText(
    card?.getAttribute('data-int-text') || card?.querySelector('.promo-card__title')?.textContent
  );
}

function getInteractionCandidate(
  target: EventTarget | null,
  selectors: ReadonlyArray<{ interaction: AnalyticsInteraction; selector: string }>
): InteractionCandidate | null {
  if (!target || typeof (target as Element).closest !== 'function') return null;

  const targetElement = target as Element;
  for (const candidate of selectors) {
    const element = targetElement.closest(candidate.selector);
    if (element) return { element, interaction: candidate.interaction };
  }

  return null;
}

function isExcludedFlipCardInteraction(
  candidate: InteractionCandidate,
  target: EventTarget | null
): boolean {
  if (candidate.interaction !== 'flip-card-front' && candidate.interaction !== 'flip-card-back') {
    return false;
  }
  if (!target || typeof (target as Element).closest !== 'function') return true;

  const interactiveElement = (target as Element).closest(
    'button, a, input, select, textarea, summary, [role="button"], [data-flip-card-ignore]'
  );
  if (!interactiveElement) return false;

  return (
    !interactiveElement.hasAttribute('data-flip-card-open') &&
    !interactiveElement.hasAttribute('data-flip-card-close')
  );
}

function hasInteractionArrow(element: Element): boolean {
  return [
    '.btn__icon--swap',
    '.accordion__link-icon--swap',
    '.search-results__read-more-icon',
    '.search-overlay__quick-link-icon',
    '.sub-industry-card-list__product-link-arrow'
  ].some(selector => Boolean(element.querySelector(selector)));
}

function getCtaLabel(element: Element): string {
  const labelSelectors = [
    '.btn__label',
    '.accordion__link-label',
    '.all-products-listing__see-more-label',
    '.search-results__read-more-label',
    '.search-overlay__quick-link-label',
    '.sub-industry-card-list__product-link-label'
  ];

  for (const selector of labelSelectors) {
    const label = getChildText(element, selector);
    if (label) return label;
  }

  return normalizeInteractionText(element.textContent);
}

function isExcludedGlobalInteraction(element: Element): boolean {
  if (element.hasAttribute('aria-disabled')) return true;

  return Boolean(element.closest('header, footer, .breadcrumbs'));
}

function isVisualBuilderCtaLinkCard(element: Element): boolean {
  const classes = element.getAttribute('class')?.split(/\s+/) ?? [];
  const isCardLink =
    classes.includes('generic-card__primary-link') ||
    classes.includes('generic-card__secondary-link');

  return isCardLink && Boolean(element.closest('.visual-builder-section--cta-link'));
}

function isTransparentOutlineInteraction(element: Element): boolean {
  const classes = element.getAttribute('class')?.split(/\s+/) ?? [];
  if (!classes.includes('btn--secondary-outline')) return false;

  const hasTransparentComponentOverride = Boolean(
    element.closest(
      '.hero-banner__secondary-btn, .cta-banner__heading-secondary-btn, .cta-banner__card-secondary-btn'
    )
  );
  if (hasTransparentComponentOverride) return true;

  if (element.closest('[data-theme="dark"], .dark, .theme-dark')) return true;

  const usesSystemTheme = Boolean(element.closest('[data-theme="system"]'));
  return Boolean(
    usesSystemTheme &&
      element.ownerDocument?.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function getCircleActionText(element: Element, expanded: boolean): string {
  const actionLabel = element.getAttribute(expanded ? 'data-more-label' : 'data-less-label');
  return normalizeInteractionText(actionLabel) || getCtaLabel(element);
}

export function buildInteractionDataLayerEvent(
  interaction: AnalyticsInteraction,
  element: Element,
  baseUrl: string | undefined = element.ownerDocument?.baseURI
): InteractionDataLayerEvent | null {
  const visibleText = normalizeInteractionText(element.textContent);
  const href = normalizeInteractionLink(getAuthoredHref(element), baseUrl);

  switch (interaction) {
    case 'flip-card-front':
    case 'flip-card-back':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_flip',
        int_text: getFlipCardTitle(element),
        int_link: 'flip',
        int_detail: interaction === 'flip-card-front' ? 'front' : 'back'
      };
    case 'story-card':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_arrow',
        int_text: getChildText(element, '.story-card__title'),
        int_link: href,
        int_detail: 'stories'
      };
    case 'events-news-card':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_arrow',
        int_text: getChildText(element, '.events-news-card__title'),
        int_link: href,
        int_detail: 'events_news'
      };
    case 'latest-news-card':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_card',
        int_text: getClosestCardTitle(element, '.latest-news-card', '.latest-news-card__title'),
        int_link: href,
        int_detail: 'news_cards'
      };
    case 'hero-carousel-toggle':
      return {
        event: 'button_click',
        event_name: 'play_pause',
        int_type: 'click',
        int_text: 'hero_carousel',
        int_link: getActiveHeroTitle(element),
        int_detail: ''
      };
    case 'hero-white-cta':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_hero_white',
        int_text: getChildText(element, '.btn__label') || visibleText,
        int_link: href,
        int_detail: ''
      };
    case 'video-hero-promo-cta':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_white',
        int_text: getPromoCardTitle(element),
        int_link: href,
        int_detail: 'register_now'
      };
    case 'banner-outline-cta': {
      const opensContactModal = element.hasAttribute('data-marketo-modal-trigger');
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_hero_outline',
        int_text: getChildText(element, '.btn__label') || visibleText,
        int_link: opensContactModal ? 'modal_open' : href,
        int_detail: opensContactModal ? 'contact_us' : ''
      };
    }
    case 'solutions-card-link':
      return {
        event: 'link_click',
        event_name: 'link_click',
        int_type: 'cta_text',
        int_text: getCtaLabel(element),
        int_link: href,
        int_detail: 'learn_more'
      };
    case 'generic-card-cta-link': {
      const label = getCtaLabel(element);
      const isLearnMore = label.toLocaleLowerCase() === 'learn more';
      return {
        event: 'link_click',
        event_name: 'link_click',
        int_type: 'cta_text',
        int_text: isLearnMore ? label : '',
        int_link: isLearnMore ? href : '',
        int_detail: isLearnMore ? 'learn_more' : ''
      };
    }
    case 'industry-product-link':
      return {
        event: 'link_click',
        event_name: 'link_click',
        int_type: 'ind_product_list',
        int_text: getCtaLabel(element),
        int_link: href,
        int_detail: 'sub_ind'
      };
    case 'accordion-trigger':
      return {
        event: 'button_click',
        event_name: 'button_accordion',
        int_type: 'cta_open_close',
        int_text: getChildText(element, '.accordion__question') || visibleText,
        int_link: '',
        int_detail: element.getAttribute('aria-expanded') === 'true' ? 'expand' : 'contract'
      };
    case 'accordion-circle-toggle': {
      const expanded = element.getAttribute('aria-expanded') === 'true';
      return {
        event: 'button_click',
        event_name: 'button_circle',
        int_type: 'cta_open_close',
        int_text: getCircleActionText(element, expanded),
        int_link: '',
        int_detail: expanded ? 'see_more' : 'contract'
      };
    }
    case 'solutions-card-circle-toggle':
    case 'listing-circle-toggle':
      return {
        event: 'button_click',
        event_name: 'button_circle',
        int_type: 'cta_open_close',
        int_text: getCtaLabel(element),
        int_link: '',
        int_detail: 'see_more'
      };
    case 'product-listing-white-cta':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_white',
        int_text: getCtaLabel(element),
        int_link: href,
        int_detail: 'prod_list'
      };
    case 'product-listing-black-cta':
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: 'cta_black',
        int_text: getCtaLabel(element),
        int_link: href,
        int_detail: 'prod_list'
      };
    case 'pricing-info-card':
      return {
        event: 'link_click',
        event_name: 'link_click',
        int_type: 'cta_text',
        int_text: getCtaLabel(element),
        int_link: href,
        int_detail: 'prod_detail'
      };
    case 'pricing-quantity-increase':
      return {
        event: 'product_click',
        event_name: 'product_click',
        int_type: 'cta_incr_quantity',
        int_text: 'increase',
        int_link: '#',
        int_detail: 'prod_detail'
      };
    case 'pricing-quantity-decrease':
      return {
        event: 'product_click',
        event_name: 'product_click',
        int_type: 'cta_decr_quantity',
        int_text: 'decrease',
        int_link: '#',
        int_detail: 'prod_detail'
      };
    case 'global-text-cta': {
      const hasArrow = hasInteractionArrow(element);
      return {
        event: 'link_click',
        event_name: 'link_click',
        int_type: 'cta_text',
        int_text: hasArrow ? getCtaLabel(element) : '',
        int_link: href,
        int_detail: hasArrow ? 'arrow' : ''
      };
    }
    case 'global-black-cta':
    case 'global-white-cta': {
      const hasArrow = hasInteractionArrow(element);
      return {
        event: 'button_click',
        event_name: 'button_click',
        int_type: interaction === 'global-black-cta' ? 'cta_black' : 'cta_white',
        int_text: getCtaLabel(element),
        int_link: href,
        int_detail: hasArrow ? 'arrow' : ''
      };
    }
    case 'header-navigation-open':
      if (element.getAttribute('aria-expanded') !== 'true') return null;
      return {
        event: 'navigation_click',
        event_name: 'navigation_header',
        int_type: 'navigation_open',
        int_text: visibleText,
        int_link: '#',
        int_detail: ''
      };
    case 'header-submenu-link':
      return {
        event: 'navigation_click',
        event_name: 'navigation_header',
        int_type: 'link_click',
        int_text: getChildText(element, '.secondary-menu-label__title-text') || visibleText,
        int_link: href,
        int_detail: ''
      };
    case 'header-cta-card':
      return {
        event: 'navigation_click',
        event_name: 'navigation_header',
        int_type: 'button_click',
        int_text: getChildText(element, '.mega-menu-icon-card__title-text') || visibleText,
        int_link: href,
        int_detail: 'cta_cards'
      };
    case 'header-promo-text':
      return {
        event: 'navigation_click',
        event_name: 'navigation_header',
        int_type: 'button_click',
        int_text: getChildText(element, '.featured-card-link__label') || visibleText,
        int_link: href,
        int_detail: 'promo_text'
      };
    case 'header-promo-media': {
      const imageAlt = element.querySelector('img')?.getAttribute('alt');
      return {
        event: 'navigation_click',
        event_name: 'navigation_header',
        int_type: 'image_click',
        int_text:
          normalizeInteractionText(imageAlt) ||
          normalizeInteractionText(element.getAttribute('aria-label')) ||
          visibleText,
        int_link: href,
        int_detail: 'promo_media'
      };
    }
    case 'breadcrumb':
      return {
        event: 'navigation_click',
        event_name: 'breadcrumb',
        int_type: 'breadcrumb',
        int_text: visibleText,
        int_link: href,
        int_detail: ''
      };
    case 'header-login':
      return {
        event: 'button_header',
        event_name: 'button_header',
        int_type: 'button_header',
        int_text: 'login',
        int_link: href,
        int_detail: ''
      };
    case 'header-cart':
      return {
        event: 'button_header',
        event_name: 'button_header',
        int_type: 'button_header',
        int_text: 'cart',
        int_link: '#',
        int_detail: ''
      };
    case 'header-language':
      return {
        event: 'button_header',
        event_name: 'button_header',
        int_type: 'language',
        int_text: visibleText,
        int_link: href,
        int_detail: ''
      };
    case 'header-search':
      return {
        event: 'button_header',
        event_name: 'button_header',
        int_type: 'button_header',
        int_text: 'search',
        int_link: '#',
        int_detail: ''
      };
    case 'footer-link':
      return {
        event: 'navigation_footer',
        event_name: 'navigation_footer',
        int_type: 'link_click',
        int_text: visibleText,
        int_link: href,
        int_detail: ''
      };
    case 'footer-social':
      return {
        event: 'navigation_footer',
        event_name: 'navigation_footer',
        int_type: 'cta_social',
        int_text: normalizeInteractionText(element.getAttribute('aria-label')) || visibleText,
        int_link: href,
        int_detail: ''
      };
  }
}

export function resolveInteractionDataLayerEvent(
  target: EventTarget | null
): InteractionDataLayerEvent | null {
  return resolveInteractionDataLayerEvents(target)[0] ?? null;
}

export function resolveInteractionDataLayerEvents(
  target: EventTarget | null
): InteractionDataLayerEvent[] {
  const specializedCandidate = getInteractionCandidate(target, SPECIALIZED_INTERACTION_SELECTORS);
  const globalCandidate = getInteractionCandidate(target, GLOBAL_INTERACTION_SELECTORS);
  const events: InteractionDataLayerEvent[] = [];

  if (specializedCandidate && !isExcludedFlipCardInteraction(specializedCandidate, target)) {
    const specializedEvent = buildInteractionDataLayerEvent(
      specializedCandidate.interaction,
      specializedCandidate.element
    );
    if (specializedEvent) events.push(specializedEvent);
  }

  const isExcludedOutline =
    globalCandidate?.interaction === 'global-white-cta' &&
    isTransparentOutlineInteraction(globalCandidate.element);
  const isExcludedBlackCta =
    globalCandidate?.interaction === 'global-black-cta' &&
    isVisualBuilderCtaLinkCard(globalCandidate.element);

  if (
    globalCandidate &&
    !isExcludedGlobalInteraction(globalCandidate.element) &&
    !isExcludedOutline &&
    !isExcludedBlackCta
  ) {
    const globalEvent = buildInteractionDataLayerEvent(
      globalCandidate.interaction,
      globalCandidate.element
    );
    if (globalEvent) events.push(globalEvent);
  }

  return events;
}

export function initializeInteractionTracking(
  targetWindow: DataLayerWindow,
  targetDocument: Document
): () => void {
  const handleClick = (event: MouseEvent): void => {
    resolveInteractionDataLayerEvents(event.target).forEach(dataLayerEvent =>
      pushDataLayerEvent(dataLayerEvent, targetWindow)
    );
    notifyProductCardSelect(event.target, targetWindow);
  };

  targetDocument.addEventListener('click', handleClick);
  return () => targetDocument.removeEventListener('click', handleClick);
}
